// Notification deep-link types + helpers (Q12 D1b hoist).
// Single source of truth for the structured `link` field that
// NotificationModel documents carry, plus the BullMQ job payload
// shape that the notifications worker consumes.
//
// Both apps/api (producer) and apps/workers (consumer) import from
// here — resolves the cross-app drift that previously had
// NotificationJobData duplicated in both apps and the model schema
// fields hand-mirrored.
//
// References:
//   docs/specs/notification-deep-link-payload.md (binding contract)
//   docs/specs/notification-emit-site-taxonomy.md (sibling spec)
//   adr/ADR-017.md (Q12 ADR)
//   docs/projects/q12-notification-deep-linking.md (Q12 brief)
import { z } from "zod";
// TASK-35: severity classification for in-app notifications.
// Persisted on NotificationModel + carried through NotificationJobData so the
// queue path (handleInApp) preserves it. Frontend helper prefers this over
// event_type-derived classification (event_type fallback retained for legacy
// records written before TASK-35).
export const NOTIFICATION_SEVERITIES = ["critical", "red", "amber", "info"];
export const NOTIFICATION_LINK_TYPES = [
    "sr", // → /sr/:target_id  (optional view: "report")
    "patient", // → /patients/:target_id
    "carer", // RESERVED in v1; emit sites use type="page" /carers (Q12 D4b)
    "invoice", // RESERVED in v1; emit sites use type="page" /billing (Q12 D5b)
    "page", // → target_path (relative URL, MAY include query string)
    "external", // → target_path (absolute URL; opens in new tab)
];
// Zod discriminated union — Q12 D9c boundary validation.
// Mongoose enum gate + this Zod schema are the two layers of
// defense around the link payload's discriminated-union shape.
export const notificationLinkSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("sr"), target_id: z.string().min(1), view: z.enum(["report"]).optional() }),
    z.object({ type: z.literal("patient"), target_id: z.string().min(1) }),
    z.object({ type: z.literal("carer"), target_id: z.string().min(1) }),
    z.object({ type: z.literal("invoice"), target_id: z.string().min(1) }),
    z.object({ type: z.literal("page"), target_path: z.string().regex(/^\//, "must be relative") }),
    z.object({ type: z.literal("external"), target_path: z.string().url() }),
]);
const ID_LINK_TYPES = ["sr", "patient", "carer", "invoice"];
function isIdLinkType(t) {
    return ID_LINK_TYPES.includes(t);
}
// Single helper used by every producer site (Q12 D8a).
// Throws synchronously on missing required fields per type so emit
// sites fail fast at write time rather than producing malformed
// link payloads that silently degrade at click time.
export function buildNotificationLink(type, opts) {
    if (isIdLinkType(type)) {
        if (!opts?.target_id)
            throw new Error(`buildNotificationLink('${type}', ...) requires target_id`);
        return type === "sr" && opts.view
            ? { type, target_id: opts.target_id, view: opts.view }
            : { type, target_id: opts.target_id };
    }
    // type ∈ { page, external } — both require target_path
    if (!opts?.target_path)
        throw new Error(`buildNotificationLink('${type}', ...) requires target_path`);
    return { type, target_path: opts.target_path };
}
// Convenience for rule-engine: derive link.type from RuleEngineJobData.record_type.
// Visit reports map to "sr" (the call site adds view: "report"); CSAT maps to "sr"
// (CSAT is SR-scoped); medication maps to "patient" (medications belong to a patient).
export function recordTypeToLinkType(recordType) {
    switch (recordType) {
        case "sr": return "sr";
        case "visit_report": return "sr";
        case "patient": return "patient";
        case "csat": return "sr";
        case "medication": return "patient";
    }
}
//# sourceMappingURL=notification.js.map